import { useQuery, type QueryClient } from '@tanstack/react-query';
import { bookServiceApi } from '@/services/api';

export type ChatHistoryContext = 'character' | 'section' | 'chapter';

export interface ChatHistoryTurn {
  chat_id: number;
  user_message: string;
  response: string;
  created_at: string | null;
}

export interface ChatHistoryResponse {
  items: ChatHistoryTurn[];
}

export const chatHistoryQueryKey = (userId: string, context: ChatHistoryContext, contextId?: number) =>
  ['chat-history', userId, context, contextId] as const;

export const useChatHistoryQuery = (
  context: ChatHistoryContext,
  contextId: number | undefined,
  enabled: boolean,
  userId: string,
) => useQuery<ChatHistoryResponse>({
  queryKey: chatHistoryQueryKey(userId, context, contextId),
  queryFn: async () => {
    const result = await bookServiceApi.get<ChatHistoryResponse>(
      `/chat-history/${context}/${contextId}?limit=100`,
    );
    if (result.success && result.data) return result.data;
    throw new Error(result.error || 'Failed to load chat history');
  },
  enabled: enabled && !!contextId,
  staleTime: 30_000,
  gcTime: 5 * 60_000,
});

export const appendChatHistoryCache = (
  queryClient: QueryClient,
  userId: string,
  context: ChatHistoryContext,
  contextId: number,
  userMessage: string,
  response: string,
) => {
  const key = chatHistoryQueryKey(userId, context, contextId);
  queryClient.setQueryData<ChatHistoryResponse>(key, previous => ({
    items: [
      ...(previous?.items ?? []),
      {
        chat_id: -Date.now(),
        user_message: userMessage,
        response,
        created_at: new Date().toISOString(),
      },
    ].slice(-100),
  }));
};
